import { useEffect } from 'react';
import { BrowserRouter, Navigate, Routes, Route, useNavigate } from 'react-router-dom';
import Home from './pages/Home';
import ProjectShell from './pages/ProjectShell';
import ProjectHome from './pages/ProjectHome';
import ProgrammeShell from './pages/ProgrammeShell';
import ProgrammeHome from './pages/ProgrammeHome';
import AdminShell from './pages/AdminShell';
import PlatformShell from './pages/PlatformShell';
import PlatformCompanies from './pages/PlatformCompanies';
import PlatformCompanyDetail from './pages/PlatformCompanyDetail';
import PlatformInvitations from './pages/PlatformInvitations';
import RaidLog from './pages/RaidLog';
import StageGates from './pages/StageGates';
import Plan from './pages/Plan';
import Resource from './pages/Resource';
import StatusReports from './pages/StatusReports';
import PillarPlaceholder from './pages/PillarPlaceholder';
import Charter from './pages/initiation/Charter';
import ProcessScope from './pages/initiation/ProcessScope';
import RequirementsRegister from './pages/initiation/RequirementsRegister';
import SolutionOptions from './pages/initiation/SolutionOptions';
import SolutionOptionDetail from './pages/initiation/SolutionOptionDetail';
import BakeOffComparison from './pages/initiation/BakeOffComparison';
import RomEstimate from './pages/initiation/RomEstimate';
import Decisions from './pages/Decisions';
import RoleProposal from './pages/RoleProposal';
import Disposition from './pages/workbench/Disposition';
import BuildItemTracker from './pages/workbench/BuildItemTracker';
import FunctionalSpecs from './pages/workbench/FunctionalSpecs';
import ProcessDesign from './pages/workbench/ProcessDesign';
import TestScripts from './pages/workbench/TestScripts';
import Defects from './pages/workbench/Defects';
import UserAdmin from './pages/UserAdmin';
import Signup from './pages/Signup';
import Platform from './pages/Platform';
import Support from './pages/Support';
import Company from './pages/Company';
import Auth from './pages/Auth';
import Blocked from './pages/Blocked';
import Onboarding from './pages/Onboarding';
import AdminLanding from './pages/AdminLanding';
import ProcessTaxonomy from './pages/admin/ProcessTaxonomy';
import GateDeliverableTemplates from './pages/admin/GateDeliverableTemplates';
import RoleTypes from './pages/admin/RoleTypes';
import FunctionalSpecSections from './pages/admin/FunctionalSpecSections';
import RoleAssignments from './pages/admin/RoleAssignments';
import EstimationDefaults from './pages/admin/EstimationDefaults';
import CostTypes from './pages/admin/CostTypes';
import DemoSettings from './pages/admin/DemoSettings';
import ReferenceData from './pages/admin/ReferenceData';
import UserLanding from './pages/UserLanding';
import ProgrammeMembers from './pages/ProgrammeMembers';
import Loader from './components/Loader';
import ProtectedRoute from './components/ProtectedRoute';
import StaffRoute from './components/StaffRoute';
import { useSupabaseAuth } from './context/AuthContext';
import { peekReturnTo, clearReturnTo } from './lib/postLoginRedirect';
import { resolvePostLoginPath } from './lib/userPreferences';
import './App.css';

function PublicHome() {
  const { loading, isAuthenticated } = useSupabaseAuth();
  if (loading) return <Loader text="Checking your session..." />;
  if (isAuthenticated) return <Navigate to="/app" replace />;
  return <Signup />;
}

/**
 * Post-auth hub: onboarding if no org, otherwise one hop to the app (default /home).
 * Optional deep link via ?next= on /auth or Auth0 appState.returnTo (see postLoginRedirect).
 */
function RoleHome() {
  const { loading, isAuthenticated, profile } = useSupabaseAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!isAuthenticated) {
      navigate('/auth?mode=signin', { replace: true });
      return;
    }
    if (!profile?.organisation_id) {
      navigate('/onboarding', { replace: true });
      return;
    }
    const next = peekReturnTo();
    navigate(resolvePostLoginPath(profile, next), { replace: true });
    requestAnimationFrame(() => {
      requestAnimationFrame(() => clearReturnTo());
    });
  }, [loading, isAuthenticated, profile, navigate]);

  return <Loader text="Opening Transformax…" />;
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<PublicHome />} />
        {/* Marketing page — was "/platform" until it collided with the staff Platform Console below
            (matchRoutes always resolved the Console branch for both "/platform" and its children,
            leaving this page dead/unreachable). Moved to "/product" rather than the console, since
            the console is the one with real internal navigation wired to "/platform" everywhere. */}
        <Route path="/product" element={<Platform />} />
        <Route path="/pricing" element={<Navigate to="/#invite" replace />} />
        <Route path="/support" element={<Support />} />
        <Route path="/company" element={<Company />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/blocked" element={<Blocked />} />
        <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
        <Route path="/app" element={<RoleHome />} />
        <Route path="/admin" element={<ProtectedRoute><AdminShell /></ProtectedRoute>}>
          <Route index element={<AdminLanding />} />
          <Route path="users" element={<UserAdmin />} />
          <Route path="taxonomy" element={<ProcessTaxonomy />} />
          <Route path="gate-templates" element={<GateDeliverableTemplates />} />
          <Route path="role-types" element={<RoleTypes />} />
          <Route path="functional-spec-sections" element={<FunctionalSpecSections />} />
          <Route path="role-assignments" element={<RoleAssignments />} />
          <Route path="estimation-defaults" element={<EstimationDefaults />} />
          <Route path="cost-types" element={<CostTypes />} />
          <Route path="demo" element={<DemoSettings />} />
          <Route path="reference-data" element={<ReferenceData />} />
        </Route>
        <Route path="/platform" element={<StaffRoute><PlatformShell /></StaffRoute>}>
          <Route index element={<PlatformCompanies />} />
          <Route path="companies/:id" element={<PlatformCompanyDetail />} />
          <Route path="invitations" element={<PlatformInvitations />} />
        </Route>
        <Route path="/workspace" element={<ProtectedRoute><UserLanding /></ProtectedRoute>} />
        <Route path="/home" element={<ProtectedRoute><Home /></ProtectedRoute>} />

        {/* Layer 2 — project workspace (four-pillar shell, project-scoped delivery) */}
        <Route path="/project/:id" element={<ProtectedRoute><ProjectShell /></ProtectedRoute>}>
          <Route index element={<ProjectHome />} />
          <Route path="initiation/charter" element={<Charter />} />
          <Route path="initiation/process-scope" element={<ProcessScope />} />
          <Route path="initiation/requirements" element={<RequirementsRegister />} />
          <Route path="initiation/solution-options" element={<SolutionOptions />} />
          <Route path="initiation/solution-options/:optionId" element={<SolutionOptionDetail />} />
          <Route path="initiation/bake-off" element={<BakeOffComparison />} />
          <Route path="initiation/platform-selection" element={<Navigate to="solution-options" replace />} />
          <Route path="initiation/solution-architecture" element={<Navigate to="solution-options" replace />} />
          <Route path="initiation/rom" element={<RomEstimate />} />
          <Route path="governance/stage-gates" element={<StageGates />} />
          <Route path="governance/raid" element={<RaidLog />} />
          <Route path="governance/decisions" element={<Decisions />} />
          <Route path="management/plan" element={<Plan />} />
          <Route path="management/roles" element={<RoleProposal />} />
          <Route path="management/resource" element={<Resource />} />
          <Route path="workbench/design/process-design" element={<ProcessDesign />} />
          <Route path="workbench/design/functional-specs" element={<FunctionalSpecs />} />
          <Route path="workbench/build/disposition" element={<Disposition />} />
          <Route path="workbench/build/tracker" element={<BuildItemTracker />} />
          <Route path="workbench/test/scripts" element={<TestScripts />} />
          <Route path="workbench/test/defects" element={<Defects />} />
          <Route path="workbench/test" element={<PillarPlaceholder title="Test management" phase="Phase 7" description="Test strategy, unit/integration tests, UAT, defects — per v0.33 Test layer." />} />
          <Route path="finance/budget" element={<PillarPlaceholder title="Budget & forecast" phase="Phase 8" description="Budget tracking, variance and reforecast." />} />
          <Route path="finance/benefits" element={<PillarPlaceholder title="Benefits" phase="Phase 8" description="Benefits register and realisation tracking." />} />
        </Route>

        {/* Layer 2 — programme rollup (cross-project reporting) */}
        <Route path="/programme/:id" element={<ProtectedRoute><ProgrammeShell /></ProtectedRoute>}>
          <Route index element={<ProgrammeHome />} />
          <Route path="initiation/charter" element={<Charter />} />
          <Route path="management/status-reports" element={<StatusReports />} />
          <Route path="settings/members" element={<ProgrammeMembers />} />
        </Route>
        <Route path="/settings" element={<Navigate to="/home" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
